import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Download, QrCode, Smartphone, Share2 } from "lucide-react";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export default function InstallPage() {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Check if app is already installed
    if (window.matchMedia("(display-mode: standalone)").matches) {
      setIsInstalled(true);
    }

    // Check if iOS
    const iOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(iOS);

    // Listen for the beforeinstallprompt event
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener("beforeinstallprompt", handler);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === "accepted") {
      setIsInstalled(true);
    }

    setDeferredPrompt(null);
  };

  const appUrl = typeof window !== "undefined" ? window.location.origin : "";

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Header */}
      <header 
        className="flex items-center justify-center py-12"
        style={{ paddingTop: 'max(3rem, calc(env(safe-area-inset-top, 0px) + 2rem))' }}
      >
        <div className="text-center">
          <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4 border border-primary/20">
            <span className="text-primary font-serif text-3xl font-semibold">
              C
            </span>
          </div>
          <h1 className="font-serif text-2xl font-semibold text-foreground">
            CONCIERGE
          </h1>
          <span className="text-xs text-primary tracking-[0.3em] uppercase">
            Services de Luxe
          </span>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 pb-12">
        <div className="w-full max-w-md space-y-8">
          {isInstalled ? (
            <div className="text-center animate-fade-in">
              <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-4">
                <Download size={32} className="text-success" />
              </div>
              <h2 className="font-serif text-xl font-medium text-foreground mb-2">
                Application installée !
              </h2>
              <p className="text-sm text-muted-foreground">
                Vous pouvez maintenant accéder à Concierge depuis votre écran d'accueil
              </p>
            </div>
          ) : (
            <>
              <div className="text-center">
                <h2 className="font-serif text-xl font-medium text-foreground mb-2">
                  Installer l'application
                </h2>
                <p className="text-sm text-muted-foreground">
                  Accédez rapidement à votre conciergerie depuis votre écran d'accueil
                </p>
              </div>

              {/* Install Button (Android/Desktop) */}
              {deferredPrompt && (
                <Button
                  onClick={handleInstall}
                  variant="gold"
                  size="xl"
                  className="w-full"
                >
                  <Download size={20} />
                  <span>Installer</span>
                </Button>
              )}

              {/* iOS Instructions */}
              {isIOS && (
                <div className="bg-card rounded-xl border border-border p-6 space-y-4">
                  <div className="flex items-center gap-3">
                    <Smartphone size={24} className="text-primary shrink-0" />
                    <div>
                      <h3 className="font-medium text-foreground">
                        Installer sur iPhone/iPad
                      </h3>
                      <p className="text-xs text-muted-foreground">
                        Suivez ces étapes pour ajouter à votre écran d'accueil
                      </p>
                    </div>
                  </div>

                  <ol className="space-y-3 text-sm">
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0 text-primary font-medium text-xs">
                        1
                      </span>
                      <span className="text-muted-foreground">
                        Appuyez sur le bouton{" "}
                        <Share2
                          size={16}
                          className="inline-block text-foreground mx-1"
                        />{" "}
                        Partager dans Safari
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0 text-primary font-medium text-xs">
                        2
                      </span>
                      <span className="text-muted-foreground">
                        Faites défiler et appuyez sur{" "}
                        <strong className="text-foreground">
                          "Sur l'écran d'accueil"
                        </strong>
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0 text-primary font-medium text-xs">
                        3
                      </span>
                      <span className="text-muted-foreground">
                        Appuyez sur{" "}
                        <strong className="text-foreground">"Ajouter"</strong> pour
                        confirmer
                      </span>
                    </li>
                  </ol>
                </div>
              )}

              {/* QR Code Section */}
              <div className="bg-card rounded-xl border border-border p-6 text-center">
                <QrCode size={32} className="text-primary mx-auto mb-3" />
                <h3 className="font-medium text-foreground mb-2">
                  Scanner pour ouvrir sur mobile
                </h3>
                <p className="text-xs text-muted-foreground mb-4">
                  Scannez ce QR code avec l'appareil photo de votre téléphone
                </p>

                {/* Placeholder for QR Code - would be generated with actual URL */}
                <div className="w-40 h-40 mx-auto bg-muted rounded-xl flex items-center justify-center border border-border">
                  <div className="text-center">
                    <QrCode size={48} className="text-muted-foreground mx-auto mb-2" />
                    <p className="text-[10px] text-muted-foreground break-all px-2">
                      {appUrl}
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="py-4 text-center safe-bottom">
        <p className="text-xs text-muted-foreground">
          © 2024 Concierge. Tous droits réservés.
        </p>
      </footer>
    </div>
  );
}